import { useState } from "react";
import axios from "axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationPopUp from "components/confirmationPopup/page";

import {
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Button,
  FormHelperText,
  IconButton,
  CircularProgress,
} from "@mui/material";
import MDAlertCloseIcon from "components/MDAlert/MDAlertCloseIcon";
import { toast } from "react-toastify";
import capitalizeWords from "utils";

const CustomNotification = () => {
  const [rows, setRows] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("loose_weight");
  const [reload, setReload] = useState(1);

  const [errors, setErrors] = useState({
    title: "",
    message: "",
    type: "",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const [fullMessageOpen, setFullMessageOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState("");

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const columns = [
    { Header: "S no.", accessor: "sno", width: "10%", align: "center" },
    { Header: "Created At", accessor: "createdAt", width: "20%", align: "center" },
    { Header: "Title", accessor: "title", align: "center" },
    { Header: "Type", accessor: "type", align: "center" },

    {
      Header: "Description (Click to view full message)",
      accessor: "body",
      align: "center",
    },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const handleOpenMessage = ({ open, content }) => {
    setSelectedMessage(content);
    setFullMessageOpen(true);
  };

  const handleClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleDelete = (id) => {
    setStoryToDelete(id);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/v1/admin/notification/${storyToDelete}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Notification deleted successfully");
      setConfirmDeleteOpen(false);
      setReload(reload + 1);
    } catch (error) {
      toast.error("Failed to delete notification");
      console.error("Delete error:", error);
    }
  };

  const fetchNotifications = async ({ pageIndex, pageSize }) => {
    try {
      const token = localStorage.getItem("token");
      const url = new URL(`${process.env.REACT_APP_API_URL}/api/v1/admin/notification`);
      const params = new URLSearchParams();

      if (pageSize) params.append("limit", pageSize);
      if (pageIndex) params.append("page", pageIndex);

      url.search = params.toString();

      const response = await axios.get(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const notifications = response.data?.data?.data || [];

      const formattedRows = notifications.map((notification, index) => ({
        sno: <div>{pageSize * pageIndex + index + 1}</div>,
        createdAt: <div>{new Date(notification.createdAt).toLocaleDateString()}</div>,
        title: <div>{notification.title || "-"}</div>,
        type: <div>{capitalizeWords(notification.goal) || "-"}</div>,

        body: (
          <div>
            {notification.body?.split(" ").length > 2 ? (
              <span>
                {notification.body.split(" ").slice(0, 2).join(" ")}...
                <Button
                  onClick={() => handleOpenMessage({ open: true, content: notification.body })}
                  size="small"
                  variant="text"
                  sx={{ textTransform: "none", ml: 1 }}
                >
                  View
                </Button>
              </span>
            ) : (
              notification.body || "Na"
            )}
          </div>
        ),
        action: (
          <IconButton color="error" onClick={() => handleDelete(notification._id)}>
            <DeleteIcon />
          </IconButton>
        ),
      }));

      return {
        data: formattedRows,
        total: response.data?.data?.count,
      };
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const validateForm = () => {
    const newErrors = {
      title: title.trim() === "" ? "Title is required" : "",
      message: message.trim() === "" ? "Message is required" : "",
      type: type === "" ? "Type is required" : "",
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some((e) => e !== "");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const payload = {
        title,
        message,
        goal: type,
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/admin/notification`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCreateDialogOpen(false);
      setTitle("");
      setMessage("");
      setType("loose_weight");
      setErrors({ title: "", message: "", type: "" });
      setReload(reload + 1);
      toast.success("Notification created successfully");
    } catch (error) {
      console.error("Error creating notification:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Card sx={{ mt: 3 }}>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Notification Management
          </MDTypography>
        </MDBox>

        <MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={{
              defaultValue: rowsPerPage,
              entries: [5, 10, 20, 50],
            }}
            fetchDataRows={fetchNotifications}
            reload={reload}
            onSearchChange={(val) => {
              setSearchQuery(val);
              setPage(0);
            }}
            canSearch={false}
            noEndBorder
            button={
              <MDButton variant="gradient" color="info" onClick={() => setCreateDialogOpen(true)}>
                Add Notification
              </MDButton>
            }
          />
        </MDBox>
      </Card>

      {/* Create Notification Modal */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ textAlign: "center" }}>Create Notification</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              fullWidth
            />
            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              error={!!errors.message}
              helperText={errors.message}
              fullWidth
              multiline
              rows={4}
            />
            <TextField
              select
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              error={!!errors.type}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value=""> </option>
              <option value="loose_weight">Loose Weight</option>
              <option value="maintain_weight">Maintain Weight</option>
              <option value="gain_weight">Gain Weight</option>
              <option value="all">All</option>
            </TextField>
            {errors.type && <FormHelperText error>{errors.type}</FormHelperText>}
          </Box>

          <Box display="flex" gap={2} mt={3}>
            {/* Cancel Button */}
            <MDButton
              variant="outlined"
              sx={{
                width: "90%",
                backgroundColor: "#E1E1E1",
                color: "gray",
                "&:hover": {
                  border: "1px solid #164275",
                  color: "#164275",
                  backgroundColor: "white",
                },
              }}
              onClick={() => setCreateDialogOpen(false)}
              disabled={loading} // disable while loading
            >
              Cancel
            </MDButton>

            {/* Submit Button with Loading */}
            <MDButton
              sx={{ width: "90%" }}
              variant="gradient"
              color="info"
              onClick={handleSubmit}
              disabled={loading} // disable while loading
            >
              {loading ? (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <CircularProgress size={20} color="inherit" />
                  Submitting...
                </Box>
              ) : (
                "Submit"
              )}
            </MDButton>
          </Box>
        </DialogContent>
      </Dialog>

      <ConfirmationPopUp
        open={confirmDeleteOpen}
        onClose={handleClose}
        onSubmit={confirmDelete}
        title={"Delete Notification"}
        content={"Are you sure you want to Delete this Notification ?"}
        description={"This action can not be reverted later."}
      />

      {/* Full Message Dialog */}
      <Dialog
        open={fullMessageOpen}
        onClose={() => setFullMessageOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Full Message
          <IconButton
            aria-label="close"
            onClick={() => setFullMessageOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <MDAlertCloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{selectedMessage}</DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CustomNotification;
